/**
 * Swap Router - Selects the appropriate swap provider based on chains
 *
 * Provider selection:
 * - Solana ↔ Solana: Jupiter
 * - TON ↔ TON: STON.fi
 * - TRON ↔ TRON: SunSwap
 * - Cross-chain (EVM/Cosmos/Solana): Skip Go
 * - Cross-chain with TON/TRON: Not yet supported (would need ChangeNOW)
 *
 * Chain IDs automatically switch between mainnet/testnet based on networkConfig.
 */

import { getChainTypes } from './networkConfig';
import { canMockRoute } from './testnetMockProvider';

export type SwapProvider = 'skipgo' | 'jupiter' | 'stonfi' | 'sunswap' | 'bttc' | 'canton' | 'unsupported';

export interface ChainInfo {
  chainId: string;
  chainType: 'evm' | 'cosmos' | 'solana' | 'ton' | 'tron' | 'canton';
}

/**
 * Get chain info from chain name (case-insensitive, trims whitespace)
 * Reads dynamically from networkConfig so chain IDs match the current network.
 */
export function getChainInfo(chainName: string): ChainInfo | null {
  const types = getChainTypes();
  return types[chainName.toLowerCase().trim()] as ChainInfo | undefined || null;
}

/**
 * Get chain type from chain name (case-insensitive, trims whitespace)
 */
export function getChainType(chainName: string): string | null {
  const types = getChainTypes();
  return types[chainName.toLowerCase().trim()]?.chainType || null;
}

/**
 * Determine which swap provider to use based on source and destination chains
 */
export function selectSwapProvider(fromChain: string, toChain: string): SwapProvider {
  const fromInfo = getChainInfo(fromChain);
  const toInfo = getChainInfo(toChain);

  if (!fromInfo || !toInfo) {
    return 'unsupported';
  }

  const fromType = fromInfo.chainType;
  const toType = toInfo.chainType;

  // Same chain type swaps
  if (fromChain === toChain || fromType === toType) {
    switch (fromType) {
      case 'solana':
        return 'jupiter';
      case 'ton':
        return 'stonfi';
      case 'tron':
        return 'sunswap';
      case 'evm':
      case 'cosmos':
        // Skip Go handles same-chain EVM and Cosmos
        return 'skipgo';
    }
  }

  // Cross-chain swaps
  // Canton bridge: Canton <-> Base (CC <-> USDC)
  if (fromType === 'canton' || toType === 'canton') {
    const otherChain = fromType === 'canton' ? toChain.toLowerCase() : fromChain.toLowerCase();
    // Canton only bridges to Base for CC <-> USDC
    if (otherChain === 'base') {
      return 'canton';
    }
    // On testnet, allow mock routes for Canton <-> Ethereum etc.
    if (canMockRoute(fromChain, toChain)) {
      return 'canton';
    }
    return 'unsupported'; // Canton only supports bridge to Base currently
  }

  // Check if TON is involved - not supported for cross-chain yet
  if (fromType === 'ton' || toType === 'ton') {
    return 'unsupported'; // Would need ChangeNOW or similar
  }

  // TRON cross-chain uses BTTC bridge (supports TRON <-> ETH/BSC)
  if (fromType === 'tron' || toType === 'tron') {
    // BTTC supports TRON <-> Ethereum and TRON <-> BSC
    const otherType = fromType === 'tron' ? toType : fromType;
    if (otherType === 'evm') {
      return 'bttc';
    }
    return 'unsupported'; // TRON <-> Solana/Cosmos not supported
  }

  // Skip Go handles cross-chain between EVM, Cosmos, and Solana
  return 'skipgo';
}

/**
 * Check if a route is supported
 */
export function isRouteSupported(fromChain: string, toChain: string): boolean {
  return selectSwapProvider(fromChain, toChain) !== 'unsupported';
}

/**
 * Get human-readable provider name
 */
export function getProviderName(provider: SwapProvider): string {
  switch (provider) {
    case 'skipgo': return 'Skip Go';
    case 'jupiter': return 'Jupiter';
    case 'stonfi': return 'STON.fi';
    case 'sunswap': return 'SunSwap';
    case 'bttc': return 'BTTC Bridge';
    case 'canton': return 'Canton Bridge';
    case 'unsupported': return 'Not Available';
  }
}

/**
 * Get reason why a route is unsupported
 */
export function getUnsupportedReason(fromChain: string, toChain: string): string {
  const fromInfo = getChainInfo(fromChain);
  const toInfo = getChainInfo(toChain);

  if (!fromInfo) {
    return `Chain "${fromChain}" is not supported`;
  }
  if (!toInfo) {
    return `Chain "${toChain}" is not supported`;
  }

  const fromType = fromInfo.chainType;
  const toType = toInfo.chainType;

  if ((fromType === 'ton' || toType === 'ton') && fromType !== toType) {
    return 'Cross-chain TON swaps not yet supported';
  }
  if ((fromType === 'tron' || toType === 'tron') && fromType !== toType) {
    return 'Cross-chain TRON swaps not yet supported';
  }
  if ((fromType === 'canton' || toType === 'canton') && fromType !== toType) {
    const otherChain = fromType === 'canton' ? toChain : fromChain;
    if (otherChain.toLowerCase() !== 'base' && !canMockRoute(fromChain, toChain)) {
      return 'Canton bridge only supports Base chain';
    }
  }

  return 'This route is not supported';
}
